import type { NavigatorCategory } from './navigator-results.types';

/**
 * How many specialties stay in the tab strip. Four keeps every care stage's
 * current tabs exactly as they are today — three would drop a tab from the
 * stages that have four.
 */
export const TAB_CATEGORY_LIMIT = 4;

export type SplitCategories = {
  /** Rendered as the tab strip, unchanged from today. */
  tabs: NavigatorCategory[];
  /** Rendered below as "Additional Providers". */
  additional: NavigatorCategory[];
  /**
   * No provider matched, so the row can only offer the Care Concierge. Grouped
   * at the end of the additional list instead of interleaved, so the gap stays
   * visible without scattering dead-ends through the list.
   */
  conciergeOnly: NavigatorCategory[];
};

/**
 * Counts the same way the rendered row does. `providerCount` is the surface's
 * own total when providers arrive lazily, so reading only `providers.length`
 * here would let the two disagree about whether a category has anyone.
 */
const hasProviders = (category: NavigatorCategory) =>
  (category.providerCount ?? category.providers.length) > 0;

type SplitOptions = {
  /**
   * Matchmaking is still running. An empty category is then "not searched yet",
   * not "nothing found", so it holds its place in the list and waits rather
   * than being written off — which also stops rows from reordering underneath
   * the reader as results land.
   */
  isMatching?: boolean;
};

export const splitCategories = (
  categories: NavigatorCategory[],
  { isMatching = false }: SplitOptions = {},
): SplitCategories => {
  const beyondTabs = categories.slice(TAB_CATEGORY_LIMIT);

  return {
    tabs: categories.slice(0, TAB_CATEGORY_LIMIT),
    additional: isMatching ? beyondTabs : beyondTabs.filter(hasProviders),
    conciergeOnly: isMatching ? [] : beyondTabs.filter((category) => !hasProviders(category)),
  };
};
